// src/hooks/useCategoryData.js
import { useEffect, useState, useCallback } from "react";
import api from "../services/apiServices";
import { useAdminStore } from '../store/adminStore'; // 1. ADDED Store

export function useCategoryData(slug) {
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api
      .get(`/admin/categories/by-slug/${slug}`)
      .then((res) => setCategory(res.data))
      .catch(() => setCategory(null))
      .finally(() => setLoading(false));
  }, [slug]);

  return { category, loading };
}

export function useAllCategoryData() {
  const locale = useAdminStore((state) => state.locale);


  const [categories, setCategories] = useState(() => {
    if (!locale) return [];
    const cacheKey = `categories_${locale}_list`;
    const cached = localStorage.getItem(cacheKey);
    return cached ? JSON.parse(cached) : [];

  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  const fetchCategories = useCallback(async () => {

    if (!locale) {
      setCategories([]);
      return;
    }

    try {
      setLoading(true);

      const res = await api.get(`/admin/categories/list`, {
        params: { locale: locale }
      });

      const cacheKey = `categories_${locale}_list`;
      localStorage.setItem(cacheKey, JSON.stringify(res.data));
      setCategories(res.data);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [locale]); // Now depends *only* on locale


  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]); // This dependency is now stable


  return { categories, loading, error, refresh: fetchCategories };
}


//All Catgeory Data id, name, slug, type, parent_id
export function useAllCategoryBasicData(requiredLocale = null) {
  // Renamed param, default null

  // State initialization depends on the requiredLocale
  const [categories, setCategories] = useState(() => {
    // Only attempt cache load if requiredLocale is provided
    if (!requiredLocale) return [];
    const cacheKey = `categories_${requiredLocale}_basic-list`;
    const cached = localStorage.getItem(cacheKey);
    return cached ? JSON.parse(cached) : [];
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch logic depends only on requiredLocale
  const fetchCategories = useCallback(async () => {
    // Don't fetch if requiredLocale isn't provided
    if (!requiredLocale) {
      setCategories([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/common/basic-list`, {
        params: { locale: requiredLocale }, // Use the requiredLocale for API call
      });
      const cacheKey = `categories_${requiredLocale}_basic-list`; // Use requiredLocale for cache
      localStorage.setItem(cacheKey, JSON.stringify(res.data));
      setCategories(res.data);
    } catch (err) {
      console.error(
        `Fetch basic categories failed for locale ${requiredLocale}:`,
        err
      );
      setError(err.message);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [requiredLocale]); // Dependency is the required locale

  // useEffect now correctly triggers when requiredLocale changes
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]); // fetchCategories depends on requiredLocale

  // Refresh function
  const refresh = useCallback(() => {
    if (requiredLocale) {
      const cacheKey = `categories_${requiredLocale}_basic-list`;
      localStorage.removeItem(cacheKey);
    }
    fetchCategories();
  }, [fetchCategories, requiredLocale]); // Include requiredLocale

  return { categories, loading, error, refresh };
}

//Fetch Catgeory Questions

export function useCategoryQuestions(categoryId) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchQuestions() {
      setLoading(true);
      setError(null);

      try {
        const res = await api.get(
          `/admin/categories/cat-qna/category/${categoryId}`,
          {
            params: { status: "draft" },
          }
        );
        setQuestions(res.data);
      } catch (err) {
        console.error("Failed to fetch questions:", err);
        setQuestions([]);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    if (!categoryId) {
      setQuestions([]);
      setLoading(false);
      setError(null);
      return;
    }

    fetchQuestions();
  }, [categoryId]);

  return { questions, loading, error };
}
